import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const HEARTBEAT_INTERVAL = 30_000; // 30 secondi

function getSessionId(): string {
  let id = sessionStorage.getItem('tracking_session_id');
  if (!id) {
    id = `s_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('tracking_session_id', id);
  }
  return id;
}

function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android.*mobile|windows phone/i.test(ua)) return 'mobile';
  return 'desktop';
}

/**
 * Hook per tracciare le visite su MySQL via Express API.
 * Invia una visita ad ogni cambio pagina e un heartbeat periodico.
 * Rileva se è un umano tramite interazione (mousemove/touchstart/scroll/click).
 */
export const useTracking = () => {
  const location = useLocation();
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionId = useRef(getSessionId());
  const humanSent = useRef(false);

  // Traccia visita ad ogni cambio pagina
  useEffect(() => {
    const page = location.pathname + location.search;

    fetch('/api/track/visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: sessionId.current,
        page,
        referrer: document.referrer || null,
        language: navigator.language?.split('-')[0] || null,
        deviceType: getDeviceType(),
        screenResolution: `${screen.width}x${screen.height}`,
      }),
    }).catch(() => {
      // Silenzioso: il tracking non deve bloccare l'utente
    });
  }, [location]);

  // Rileva interazione umana: almeno 3 secondi di eventi prima di confermare
  useEffect(() => {
    const HUMAN_THRESHOLD_MS = 3_000;
    let firstEventAt: number | null = null;

    function onInteraction() {
      if (humanSent.current) return;

      const now = Date.now();
      if (firstEventAt === null) {
        firstEventAt = now;
        return;
      }

      if (now - firstEventAt >= HUMAN_THRESHOLD_MS) {
        humanSent.current = true;

        fetch('/api/track/human', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: sessionId.current }),
        }).catch(() => {});

        document.removeEventListener('mousemove', onInteraction);
        document.removeEventListener('touchstart', onInteraction);
        document.removeEventListener('scroll', onInteraction);
        document.removeEventListener('click', onInteraction);
      }
    }

    document.addEventListener('mousemove', onInteraction, { passive: true });
    document.addEventListener('touchstart', onInteraction, { passive: true });
    document.addEventListener('scroll', onInteraction, { passive: true });
    document.addEventListener('click', onInteraction, { passive: true });

    return () => {
      document.removeEventListener('mousemove', onInteraction);
      document.removeEventListener('touchstart', onInteraction);
      document.removeEventListener('scroll', onInteraction);
      document.removeEventListener('click', onInteraction);
    };
  }, []);

  // Heartbeat periodico per calcolo durata sessione
  useEffect(() => {
    heartbeatRef.current = setInterval(() => {
      fetch('/api/track/heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionId.current }),
      }).catch(() => {});
    }, HEARTBEAT_INTERVAL);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, []);

  // Heartbeat finale all'uscita (tab nascosta o chiusura browser)
  useEffect(() => {
    const sendFinalHeartbeat = () => {
      const blob = new Blob(
        [JSON.stringify({ sessionId: sessionId.current })],
        { type: 'application/json' }
      );
      navigator.sendBeacon('/api/track/heartbeat', blob);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendFinalHeartbeat();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', sendFinalHeartbeat);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', sendFinalHeartbeat);
    };
  }, []);
};
